import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/** The mode on screen. */
export type Theme = 'light' | 'dark';

/** The mode the user chose: System follows the operating system. */
export type ThemePreference = 'system' | Theme;

interface ThemeState {
  /** The mode on screen, resolved from the preference. */
  mode: Theme;
  preference: ThemePreference;
}

const updateFavicon = (theme: Theme) => {
  const favicon = document.querySelector(
    'link[rel="icon"]'
  ) as HTMLLinkElement | null;
  if (favicon) {
    // In dark mode, use white/light favicon; in light mode, use dark/colored favicon
    favicon.href =
      theme === 'dark' ? '/favicon-light.png' : '/favicon-dark.png';
  }
};

/** The operating system's mode, light where it cannot say. */
const getSystemTheme = (): Theme =>
  window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

/**
 * The saved preference. Nothing saved is System, which resolves at boot as
 * the fallback always did.
 */
const getInitialPreference = (): ThemePreference => {
  try {
    const stored = localStorage.getItem('theme');
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // Storage blocked: follow the system.
  }
  return 'system';
};

const resolveTheme = (preference: ThemePreference): Theme =>
  preference === 'system' ? getSystemTheme() : preference;

/** Tailwind's `.dark` on <html>, and the favicon to match. */
const applyTheme = (theme: Theme) => {
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  updateFavicon(theme);
};

const storePreference = (preference: ThemePreference) => {
  try {
    localStorage.setItem('theme', preference);
  } catch {
    // Storage blocked: the choice lasts until the page reloads.
  }
};

const initialPreference = getInitialPreference();

// Before React renders, so the first paint is in the right mode.
applyTheme(resolveTheme(initialPreference));

const initialState: ThemeState = {
  mode: resolveTheme(initialPreference),
  preference: initialPreference,
};

/** Takes a preference and the mode it puts on screen. */
const choose = (
  state: ThemeState,
  preference: ThemePreference,
  mode: Theme
) => {
  state.preference = preference;
  state.mode = mode;
  storePreference(preference);
  applyTheme(mode);
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    /** An explicit mode, as the account menu sets it. */
    setTheme: (state, action: PayloadAction<Theme>) => {
      choose(state, action.payload, action.payload);
    },
    /**
     * The other mode from the one on screen, made explicit: from System it
     * offers the opposite of what the system shows.
     */
    toggleTheme: (state) => {
      const next = state.mode === 'dark' ? 'light' : 'dark';
      choose(state, next, next);
    },
    /** System, Light or Dark, as the appearance picker sets it. */
    setThemePreference: {
      reducer: (
        state,
        action: PayloadAction<{ preference: ThemePreference; system: Theme }>
      ) => {
        const { preference, system } = action.payload;
        choose(
          state,
          preference,
          preference === 'system' ? system : preference
        );
      },
      prepare: (preference: ThemePreference) => ({
        payload: { preference, system: getSystemTheme() },
      }),
    },
    /** The operating system switched; the screen follows only in System. */
    systemThemeChanged: (state, action: PayloadAction<Theme>) => {
      if (state.preference !== 'system' || state.mode === action.payload)
        return;
      state.mode = action.payload;
      applyTheme(action.payload);
    },
  },
});

export const { setTheme, toggleTheme, setThemePreference, systemThemeChanged } =
  themeSlice.actions;

export const themeReducer = themeSlice.reducer;

export const selectTheme = (state: { theme: ThemeState }) => state.theme.mode;

export const selectThemePreference = (state: { theme: ThemeState }) =>
  state.theme.preference;

export const selectThemeColors = (state: { theme: ThemeState }) => {
  const theme = state.theme.mode;
  return theme === 'dark' ? darkColors : lightColors;
};

const darkColors = {
  background: '#0A0A0B',
  backgroundSecondary: '#0F0F10',
  surface: 'rgba(255, 255, 255, 0.02)',
  surfaceHover: 'rgba(255, 255, 255, 0.04)',
  border: 'rgba(255, 255, 255, 0.05)',
  borderHover: 'rgba(255, 255, 255, 0.08)',
  text: '#FFFFFF',
  textSecondary: '#9CA3AF',
  textTertiary: '#6B7280',
  primary: '#3E8BFF',
  primaryHover: '#3E8BFF',
};

const lightColors = {
  background: '#F8F9FA',
  backgroundSecondary: '#FFFFFF',
  surface: 'rgba(255, 255, 255, 0.7)',
  surfaceHover: 'rgba(255, 255, 255, 0.85)',
  border: 'rgba(0, 0, 0, 0.06)',
  borderHover: 'rgba(0, 0, 0, 0.09)',
  text: '#1A1A1A',
  textSecondary: '#6B7280',
  textTertiary: '#9CA3AF',
  primary: '#3E8BFF',
  primaryHover: '#2D7AED',
};
